import { sentry } from '$shared/helpers/logger/sentry';
import ErrorStatus from '$shared/constants/ErrorStatus';
import { CustomServiceErrorMessage, LocalStorageKey } from '$shared/constants/global';
import { OtherExpection } from '$shared/exceptions/OtherExpection';
import { dispatchEvent } from '$shared/lib/events/dispatchEvent';
import { isJsonString } from '$shared/helpers/utils';
import glabalAuthData from '$shared/lib/api/glabalAuthData';

const JsonErrorContentType = ['application/json', `application/problem+json`];
const MessageTemplateKey = 'messageTemplate';

export default class ApiError extends Error {
  status: number;
  errors?: Record<string, Array<string>> | undefined;

  constructor(
    status: number,
    statusText: string,
    title?: string,
    validationErrors?: Record<string, Array<string>>,
    detail?: string
  ) {
    super();
    this.status = status;
    this.message =
      this.validationError(validationErrors) ||
      [title, detail].filter(Boolean).join(' - ') ||
      statusText;
    this.errors = validationErrors;
    dispatchEvent.byStatus(status);
  }

  private parseErrorMessageTemplate(validationErrors: Record<string, Array<string>>): string {
    let message = validationErrors[MessageTemplateKey]?.first() || '';
    if (!message) return '';

    for (const param of Object.keys(validationErrors)) {
      if (param === MessageTemplateKey) continue;
      const paramValue =
        (Array.isArray(validationErrors[param]) && validationErrors[param].join(', ')) || '';
      message = message.replace(`{${param}}`, paramValue);
    }
    return message;
  }

  private validationError(validationErrors?: Record<string, Array<string>>): string | undefined {
    if (!validationErrors) return;

    const keys = Object.keys(validationErrors);
    // If validation Errors have messageTemplate then need to parse the message template
    if (keys.length > 1 && validationErrors[MessageTemplateKey]) {
      return this.parseErrorMessageTemplate(validationErrors);
    }
    const key = keys.pop();
    if (CustomServiceErrorMessage[Object.keys(validationErrors)[0]]) {
      return `${CustomServiceErrorMessage[Object.keys(validationErrors)[0]]} ${
        validationErrors[key][0]
      }`;
    }
    return CustomServiceErrorMessage[validationErrors[key][0]] || validationErrors[key][0];
  }

  static fromXhr(xhr: XMLHttpRequest): ApiError {
    // Used for sentry logs to check the requested infos
    const data: ObjectType<any> = {
      status: xhr.status,
      url: xhr.responseURL,
      body: xhr.responseText,
      response: xhr.response
    };

    try {
      const isJsonText: boolean = isJsonString(xhr.response);
      const errorData = isJsonText ? JSON.parse(xhr.response) : {};
      if (!isJsonText) sentry.warn('XHR Http response is not JSON string', data);
      return new ApiError(xhr.status, xhr.statusText, errorData?.title, errorData?.errors);
    } catch (err) {
      const exception = OtherExpection.from('API Error', 'fromXhr', err, data);
      sentry.log(exception);
      return new ApiError(xhr.status, xhr.statusText);
    }
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    // Created a clone for the response to use in catch block.
    const cloneResponse: Response = response?.clone();
    try {
      const contentType: string = response?.headers?.get('content-type')?.split(';')?.first();
      // Checked for json contet type and convert the response into json.
      const errorData: ObjectType<any> =
        (JsonErrorContentType?.includes(contentType) && (await response?.json())) || {};
      if (glabalAuthData.accessToken && response.status === ErrorStatus.UnAuthourized) {
        window.localStorage.setItem(LocalStorageKey.UserSession, JSON.stringify({ expired: true }));
      }
      return new ApiError(
        response?.status,
        response?.statusText,
        errorData?.title,
        errorData?.errors,
        errorData?.detail || errorData?.error_description
      );
    } catch (err) {
      // Can't reach server
      if (response instanceof Error) {
        return new ApiError(ErrorStatus.BadRequest, response?.message);
      }
      // Used cloned response to avoid the use of already read or locked body stream
      const responseBody: string = await cloneResponse?.text();
      const exception = OtherExpection.from('ApiError', 'fromResponse', err, {
        url: response?.url,
        body: responseBody
      });
      sentry.log(exception);
      return new ApiError(response?.status, response?.statusText);
    }
  }
}
